/**
 * Consultant Routes Configuration
 *
 * Defines all routes for consultant user pages.
 */

import type { ReactElement } from 'react';
import { BarChart3, Users, ClipboardList, FileText, Receipt } from 'lucide-react';

// Consultant Pages
import { ConsultantDashboardPage } from '../pages/consultant/ConsultantDashboardPage';
import { ConsultantClientListPage } from '../pages/consultant/ConsultantClientListPage';
import { ConsultantClientDetailPage } from '../pages/consultant/ConsultantClientDetailPage';
import { PreAuditListPage } from '../pages/consultant/PreAuditListPage';
import { PreAuditChecklistPage } from '../pages/consultant/PreAuditChecklistPage';
import { CARGenerationPage } from '../pages/consultant/CARGenerationPage';
import { ConsultantReportsPage } from '../pages/consultant/ConsultantReportsPage';
import { ConsultantSettingsPage } from '../pages/consultant/ConsultantSettingsPage';
import { InvoiceListPage } from '../pages/consultant/InvoiceListPage';
import { InvoiceDetailPage } from '../pages/consultant/InvoiceDetailPage';

/**
 * Route definition interface
 */
export interface RouteDefinition {
  path: string;
  element: ReactElement;
  label: string;
  icon?: ReactElement;
  showInNav?: boolean;
  children?: RouteDefinition[];
}

/**
 * Consultant routes configuration
 */
export const consultantRoutes: RouteDefinition[] = [
  {
    path: '/consultant/dashboard',
    element: <ConsultantDashboardPage />,
    label: 'Dashboard',
    icon: <BarChart3 className="w-5 h-5" />,
    showInNav: true,
  },
  {
    path: '/consultant/clients',
    element: <ConsultantClientListPage />,
    label: 'Clients',
    icon: <Users className="w-5 h-5" />,
    showInNav: true,
    children: [
      {
        path: '/consultant/clients/:id',
        element: <ConsultantClientDetailPage />,
        label: 'Client Details',
        showInNav: false,
      },
    ],
  },
  {
    path: '/consultant/pre-audits',
    element: <PreAuditListPage />,
    label: 'Pre-Audits',
    icon: <ClipboardList className="w-5 h-5" />,
    showInNav: true,
    children: [
      {
        path: '/consultant/pre-audit/:id',
        element: <PreAuditChecklistPage />,
        label: 'Pre-Audit Checklist',
        showInNav: false,
      },
    ],
  },
  {
    path: '/consultant/reports',
    element: <ConsultantReportsPage />,
    label: 'Reports',
    icon: <FileText className="w-5 h-5" />,
    showInNav: true,
    children: [
      {
        path: '/consultant/reports/car',
        element: <CARGenerationPage />,
        label: 'CAR Generation',
        showInNav: false,
      },
      {
        path: '/consultant/reports/list',
        element: <ConsultantReportsPage />,
        label: 'Reports List',
        showInNav: false,
      },
    ],
  },
  {
    path: '/consultant/invoices',
    element: <InvoiceListPage />,
    label: 'Invoices',
    icon: <Receipt className="w-5 h-5" />,
    showInNav: true,
    children: [
      {
        path: '/consultant/invoices/:id',
        element: <InvoiceDetailPage />,
        label: 'Invoice Details',
        showInNav: false,
      },
    ],
  },
  {
    path: '/consultant/settings',
    element: <ConsultantSettingsPage />,
    label: 'Settings',
    showInNav: true,
  },
];

/**
 * Get all routes (flattened)
 */
export function getAllConsultantRoutes(): RouteDefinition[] {
  const routes: RouteDefinition[] = [];

  function flattenRoutes(routeList: RouteDefinition[]) {
    routeList.forEach((route) => {
      routes.push(route);
      if (route.children) {
        flattenRoutes(route.children);
      }
    });
  }

  flattenRoutes(consultantRoutes);
  return routes;
}

/**
 * Get navigation items (routes shown in sidebar)
 */
export function getConsultantNavigationItems(): RouteDefinition[] {
  return consultantRoutes.filter((route) => route.showInNav);
}

/**
 * Get route by path
 */
export function getConsultantRouteByPath(path: string): RouteDefinition | undefined {
  const allRoutes = getAllConsultantRoutes();
  return allRoutes.find((route) => route.path === path);
}

export default consultantRoutes;